import React, { useState, useEffect, useCallback } from 'react';
import './CardImageCarousel.css';

const CardImageCarousel = ({ images, altText = 'Image' }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    // Transform images to URLs - filter out any invalid entries
    const imageUrls = (images || [])
        .filter(img => img !== null && img !== undefined)
        .map(img => {
            if (typeof img === 'string') {
                return img;
            }
            return img.url || img.src || img;
        })
        .filter(url => url && url.trim() !== ''); // Filter out empty strings

    const nextSlide = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % imageUrls.length);
    }, [imageUrls.length]);

    const prevSlide = () => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + imageUrls.length) % imageUrls.length);
    };

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    useEffect(() => {
        if (!isHovered && imageUrls.length > 1) {
            const interval = setInterval(nextSlide, 4000);
            return () => clearInterval(interval);
        }
    }, [isHovered, nextSlide, imageUrls.length]);

    if (!imageUrls || imageUrls.length === 0) {
        return null;
    }

    // Single image - no carousel needed
    if (imageUrls.length === 1) {
        return (
            <div className="card-image-carousel-container">
                <div className="card-image-single">
                    <img src={imageUrls[0]} alt={altText} loading="lazy" />
                </div>
            </div>
        );
    }

    // Multiple images - show carousel
    return (
        <div
            className="card-image-carousel-container"
            dir="ltr"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="card-image-carousel-wrapper">
                <div
                    className="card-image-carousel-slides"
                    style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                    {imageUrls.map((imgUrl, index) => (
                        <div key={index} className="card-image-carousel-slide">
                            <img src={imgUrl} alt={`${altText} ${index + 1}`} loading="lazy" />
                        </div>
                    ))}
                </div>

                {/* Navigation Arrows */}
                <button 
                    className="card-image-carousel-arrow prev" 
                    onClick={(e) => {
                        e.stopPropagation();
                        prevSlide();
                    }}
                    aria-label="Previous image"
                >
                    &#10094;
                </button>
                <button 
                    className="card-image-carousel-arrow next" 
                    onClick={(e) => {
                        e.stopPropagation();
                        nextSlide();
                    }}
                    aria-label="Next image"
                >
                    &#10095;
                </button>

                {/* Dots */}
                <div className="card-image-carousel-dots">
                    {imageUrls.map((_, index) => (
                        <button
                            key={index}
                            className={`card-image-carousel-dot ${currentIndex === index ? 'active' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                goToSlide(index);
                            }}
                            aria-label={`Go to image ${index + 1}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CardImageCarousel;

